import React, { useCallback, useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { isPermitted } from '@plerionhq/design-system';
import {
  useOrganizationUsageSummary,
  getDefaultMonthStart,
  getToday,
} from '../../../../api/pleri-usage';
import { useUserProfile } from '../../../../api/profile';

import { useTranslation } from 'react-i18next';

const USAGE_DISPLAY_THRESHOLD = 80;
const DISMISS_KEY_PREFIX = 'pleri-usage-value-dismissed';
const USAGE_VIEW_PERMISSION = 'pleri:OrgUsage';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function isDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function storeDismiss(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    return;
  }
}

export const UsageValueBanner: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const period = useMemo(
    () => ({
      startDate: getDefaultMonthStart(),
      endDate: getToday(),
    }),
    [],
  );

  const params = useMemo(() => ({ ...period }), [period]);

  const { data: response, isLoading } = useOrganizationUsageSummary(params);
  const summary = response?.data;

  const { data: userProfile } = useUserProfile();
  const permissions = userProfile?.permissions || [];
  const canViewUsage = isPermitted(permissions, USAGE_VIEW_PERMISSION, false);

  const orgId = summary?.organizationId;
  const month = getCurrentMonth();
  const usedPercent = summary?.tier?.limit?.usedPercent ?? 0;
  const totalConversations = summary?.summary?.totalConversations ?? 0;

  const dismissKey = `${DISMISS_KEY_PREFIX}:${orgId ?? 'unknown'}:${month}`;
  const alreadyDismissed = useMemo(
    () => (orgId ? isDismissed(dismissKey) : true),
    [dismissKey, orgId],
  );
  const [manuallyDismissed, setManuallyDismissed] = useState(false);
  const [trackedDismissKey, setTrackedDismissKey] = useState(dismissKey);
  if (dismissKey !== trackedDismissKey) {
    setTrackedDismissKey(dismissKey);
    setManuallyDismissed(false);
  }

  const handleDismiss = useCallback(() => {
    storeDismiss(dismissKey);
    setManuallyDismissed(true);
  }, [dismissKey]);

  if (isLoading || !orgId) {
    return null;
  }

  const shouldShow =
    usedPercent >= USAGE_DISPLAY_THRESHOLD &&
    totalConversations > 0 &&
    !alreadyDismissed &&
    !manuallyDismissed;

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      className="mx-auto w-full max-w-3xl px-4 py-1.5"
      role="status"
      aria-live="polite"
    >
      <div className="group relative overflow-hidden rounded-xl border border-gray-200/80 bg-gradient-to-r from-white/80 to-gray-50/40 backdrop-blur-2xl dark:border-gray-700/80 dark:from-gray-900/80 dark:to-gray-800/30">
        <div className="px-3.5 py-2.5">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-100 to-orange-50 text-orange-500 ring-1 ring-orange-200/40"
              aria-hidden
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold tracking-[-0.01em] text-gray-900 dark:text-gray-100">
                {t('youHaveRunConversationsWithPleriThisMonth', {
                  count: totalConversations,
                })}
              </p>
              {canViewUsage && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  <a
                    href="/admin/pleri-usage"
                    className="font-semibold text-gray-600 underline decoration-gray-400 underline-offset-2 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  >
                    {t('viewUsage')}
                  </a>
                </p>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 rounded-md p-1 text-gray-300 transition-colors hover:text-gray-500 dark:hover:text-gray-400"
              aria-label={t('dismiss')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
